import axios from "axios";
import * as clipboard from "clipboard-polyfill";
import toast from "react-hot-toast";
import ErrorMsg from "./ErrorMsg";

/**
 * Updates a state value from an input event or raw value, with optional Redux dispatch.
 * @param {Function} setState - React state setter
 * @param {Object|*} e - Input change event or raw value
 * @param {Function} up_props - Redux action creator
 * @param {Function} dispatch - Redux dispatch function
 */
export const changeFields = (setState, e, up_props, dispatch) => {
    let value;
    try {
        value = e.target.value;
    } catch {
        value = e;
    }

    setState && setState(value);
    if (dispatch) {
        dispatch(up_props(value));
    }
};

/**
 * Copies text to clipboard and shows a success toast.
 * @param {number} index - Reference index for tracking which item was copied
 * @param {string} textToCopy - Text to copy to clipboard
 * @param {Function} setRefIndexCopied - State setter for the copied reference index
 */
export const copy = (index, textToCopy, setRefIndexCopied) => {
    clipboard.writeText(textToCopy).then(() => {
        setRefIndexCopied(index);
        toast.success("Vous avez copier la reference");
    });
};

/**
 * Searches for artists by name via the admin API.
 * @param {Object} e - Input change event
 * @param {Function} setLoading - Loading state setter
 * @param {Function} setSearchResult - Search result state setter
 */
export const searchArtist = async (e, setLoading, setSearchResult) => {
    let str = e.target.value;
    if (str) {
        setLoading(true);
        await axios.get("/admin/users/search_artist/" + str).then((response) => {
            let data = response.data;
            setSearchResult(data);
            setLoading(false);
        });
    }
};

/**
 * Detects file type from a URL based on its extension.
 * @param {string} url - File URL to analyze
 * @returns {string} File type category (image, document, video, audio, archive, etc.)
 */
export const detectFileType = (url) => {
    const extension = url.split(".").pop().toLowerCase();

    const fileTypes = {
        jpg: "image", jpeg: "image", png: "image", gif: "image",
        bmp: "image", webp: "image", svg: "image",
        pdf: "document", doc: "document", docx: "document",
        xls: "spreadsheet", xlsx: "spreadsheet", csv: "spreadsheet",
        ppt: "presentation", pptx: "presentation",
        txt: "text",
        mp4: "video", mov: "video", avi: "video", mkv: "video",
        mp3: "audio", wav: "audio", ogg: "audio",
        zip: "archive", rar: "archive", "7z": "archive",
    };

    return fileTypes[extension] || "unknown";
};

/**
 * Removes standard and custom keys from an object (mutates the object).
 * @param {Object} object - Object to clean
 * @param {Array<string>} special_keys - Additional keys to remove
 * @returns {Object} The mutated object with keys removed
 */
export const deleteInObject = (object, special_keys = []) => {
    const defaultKeys = ["id", "created_at", "modified_at"];

    defaultKeys.forEach((key) => {
        try { delete object[key]; } catch (e) { /* Ignore errors */ }
    });

    if (Array.isArray(special_keys)) {
        special_keys.forEach((key) => {
            try { delete object[key]; } catch (e) { /* Ignore errors */ }
        });
    }

    return object;
};

/**
 * Converts a duration value to minutes based on its unit.
 * @param {number} value - Duration value
 * @param {string} unit - Unit type (song, song(s), hours, min)
 * @returns {number} Duration in minutes (or raw value for songs)
 * @throws {Error} If unit is unknown
 */
export const convertUnit = (value, unit) => {
    if (["song", "song(s)"].includes(unit)) return value;
    if (unit === "hours") return value * 60;
    if (unit === "min") return value;
    throw new Error(`Unknown unit : ${unit}`);
};

/**
 * Formats a numeric duration into a human-readable label with unit.
 * @param {number} x - Duration value in minutes or song count
 * @param {boolean} isArtistMusician - Whether the artist is a musician (uses songs instead of time)
 * @returns {string} Formatted label like "30 min", "2 h", or "3 Song(s)"
 */
export const labelFormatDecode = (x, isArtistMusician) => {
    let value = x;
    let unity = "min";

    if (!isArtistMusician && x >= 60) {
        unity = "h";
        value = Math.min(Math.ceil(x / 60), 5);
    } else if (isArtistMusician) {
        unity = value > 1 ? "Song(s)" : "Song";
    }

    return value + " " + unity;
};

/**
 * Extracts a user-friendly error message from an Axios error response.
 * @param {Object} error - Axios error object
 * @returns {Object} Error object with { error: boolean, message: string }
 */
export const checkErrorMessage = (error) => {
    try {
        if (error.response.data.email) {
            return { error: true, message: error.response.data.email[0] };
        }
        let message = JSON.stringify(error.response.data).replace(/"/g, "");
        return ErrorMsg(message);
    } catch (e) {
        return { error: true, message: "Erreur interne" };
    }
};

/**
 * Generates a random alphanumeric string of the given length.
 * @param {number} length - Desired string length
 * @returns {string} Random alphanumeric string
 */
export const makeId = (length) => {
    let result = "";
    const characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    const charactersLength = characters.length;
    for (let i = 0; i < length; i++) {
        result += characters.charAt(Math.floor(Math.random() * charactersLength));
    }
    return result;
};

/**
 * Resolves the currency symbol for a given address based on country list.
 * @param {string} addressOfEvent - Event address string (comma-separated, country last)
 * @param {Array} countryAllowed - List of country objects with name, nativeName, and currencies
 * @returns {string|undefined} Currency symbol or undefined if not found
 */
export const currencyByAddress = (addressOfEvent, countryAllowed) => {
    let cur, countryTmp;
    try {
        let tmp = addressOfEvent?.split(", ");
        countryTmp = tmp[tmp?.length - 1];
    } catch {
        countryTmp = addressOfEvent;
    }
    /* eslint-disable-next-line array-callback-return */
    countryAllowed.map((tmpCountry) => {
        if (countryTmp.toLowerCase() === tmpCountry["name"].toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "")
            || countryTmp.toLowerCase() === tmpCountry["nativeName"].toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "")) {
            cur = tmpCountry?.currencies?.symbol;
        }
    });
    return cur;
};
